import { useCallback, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import FileDropInput from "../UI/FileDropInput";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import Status from "../UI/Status";
import { Table } from "../UI/Table";
import IconButton from "../UI/IconButton";
import { IconFile, IconX } from "@tabler/icons-react";
// @ts-expect-error pure js
import { formatFileSize } from "../../api/Functions";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import Spinner from "../UI/Spinner";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { currencyFormatter, dateConvert } from "../../api/Functions";
import {
  useBalanceTypeFamilies,
  useBalanceTypes,
  // @ts-expect-error pure js
} from "../../hooks/use-balances";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import {
  getBalanceTypeOptions,
  groupBalanceTypeOptionsByFamily,
} from "../../utils/balances";
import { BANK_AFFECTATION_TYPE_FILTERS } from "../../utils/constants/balances";
import useBankStatementImport, {
  type CommitResult,
  type CommitTransactionInput,
  type PreviewItem,
  type PreviewSuggestion,
} from "../../hooks/use-bank-statement-import";
import type { Bank } from "../../models/bank";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import type {
  BalanceTypeCatalog,
  BalanceTypeFamily,
} from "../../models/balance";
import { PropertyType } from "../../models/property";
import { Selectable } from "../UI/Selectable";
import styles from "./BankStatementImportDialog.module.css";

interface AffectationOption {
  value: string;
  label: string;
  domaine: "tenant" | "property" | "folder";
  id_tenant: number | null;
  id_bien: number | null;
  id_property_folder: number | null;
}

interface TypeOption {
  value: string;
  label: string;
}

type Step = "upload" | "preview";

interface BankStatementImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  banks: Bank[];
  onImported: () => void;
}

type PreviewRowState = PreviewItem;

const EMPTY_SUGGESTION: PreviewSuggestion = {
  type: null,
  domaine: null,
  id_tenant: null,
  id_bien: null,
  id_property_folder: null,
  source: "none",
};

const ACCEPT = ".pdf";

export default function BankStatementImportDialog({
  open,
  onOpenChange,
  banks,
  onImported,
}: BankStatementImportDialogProps) {
  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [idBank, setIdBank] = useState<number | null>(null);
  const [rows, setRows] = useImmer<PreviewRowState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [importResult, setImportResult] = useState<CommitResult | null>(null);

  const { preview, commit } = useBankStatementImport();
  const { balanceTypes } = useBalanceTypes() as {
    balanceTypes: BalanceTypeCatalog | undefined;
  };
  const { families } = useBalanceTypeFamilies() as {
    families: BalanceTypeFamily[];
  };
  const { tenants } = useTenants() as { tenants: Tenant[] };
  const { properties, propertiesById } = useProperties({ withById: true }) as {
    properties: Property[];
    propertiesById: Record<number, Property>;
  };
  const { propertyFolders } = usePropertyFolders() as {
    propertyFolders: PropertyFolder[];
  };

  // Types de balance autorisés pour une affectation donnée — même logique que
  // BalanceTypeSelect (un type ne fait sens que dans le contexte d'un domaine).
  const typeOptionsForDomaine = useCallback(
    (domaine: string | null): TypeOption[] =>
      domaine
        ? getBalanceTypeOptions(
            domaine,
            { balanceTypes },
            BANK_AFFECTATION_TYPE_FILTERS,
          )
        : [],
    [balanceTypes],
  );

  const affectationOptions: AffectationOption[] = useMemo(() => {
    const opts: AffectationOption[] = [];
    for (const tenant of tenants ?? []) {
      const property = propertiesById?.[tenant.id_bien];
      opts.push({
        value: `tenant:${tenant.id}`,
        label: property
          ? `Locataire — ${tenant.nom} (${property.nom})`
          : `Locataire — ${tenant.nom}`,
        domaine: "tenant",
        id_tenant: tenant.id,
        id_bien: null,
        id_property_folder: null,
      });
    }
    for (const property of properties ?? []) {
      opts.push({
        value: `property:${property.id}`,
        label: `Bien — ${property.nom}`,
        domaine: "property",
        id_tenant: null,
        id_bien: property.id,
        id_property_folder: null,
      });
    }
    for (const folder of propertyFolders ?? []) {
      // Un immeuble (dossier non comptable) ne peut pas recevoir une balance
      // directement : il faut répartir la transaction sur ses biens. On ne
      // propose donc ici que les dossiers comptables, comme AffectationSelect.
      if (folder.property_type !== PropertyType.BIEN_COMPTABLE) continue;
      opts.push({
        value: `folder:${folder.id}`,
        label: `Dossier — ${folder.nom}`,
        domaine: "folder",
        id_tenant: null,
        id_bien: null,
        id_property_folder: folder.id,
      });
    }
    return opts;
  }, [tenants, properties, propertiesById, propertyFolders]);

  function affectationValue(row: PreviewRowState): AffectationOption | null {
    const { suggestion } = row;
    if (suggestion.id_tenant) {
      return (
        affectationOptions.find(
          (opt) => opt.value === `tenant:${suggestion.id_tenant}`,
        ) ?? null
      );
    }
    if (suggestion.id_bien) {
      return (
        affectationOptions.find(
          (opt) => opt.value === `property:${suggestion.id_bien}`,
        ) ?? null
      );
    }
    if (suggestion.id_property_folder) {
      return (
        affectationOptions.find(
          (opt) => opt.value === `folder:${suggestion.id_property_folder}`,
        ) ?? null
      );
    }
    return null;
  }

  const bankOptions = useMemo(
    () => [
      { value: null as number | null, label: "Sans banque (compte manuel)" },
      ...banks.map((bank) => ({
        value: bank.id as number | null,
        label: bank.nom || bank.bank_metadata?.name || `Banque #${bank.id}`,
      })),
    ],
    [banks],
  );

  function resetAll() {
    setStep("upload");
    setFiles([]);
    setIdBank(null);
    setRows([]);
    setError("");
    setImportResult(null);
  }

  function addFiles(newFiles: File[]) {
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of newFiles) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          merged.push(f);
          seen.add(key);
        }
      }
      return merged;
    });
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handlePreview() {
    if (files.length === 0) {
      setError("Sélectionnez au moins un fichier");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const items = await preview(files, idBank);
      setRows(items);
      setStep("preview");
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setIsLoading(false);
    }
  }

  function resetCategorisation(checkedIds: (string | number)[]) {
    const ids = new Set(checkedIds);
    setRows((draft) => {
      for (const row of draft) {
        if (ids.has(row.tmp_id)) {
          row.suggestion = { ...EMPTY_SUGGESTION };
        }
      }
    });
  }

  async function handleCommit(checkedIds: (string | number)[]) {
    setError("");
    const ids = new Set(checkedIds);
    const transactions: CommitTransactionInput[] = rows
      .filter((row) => ids.has(row.tmp_id))
      .map((row) => {
        // On ne crée une balance que pour une affectation résolue (locataire,
        // bien ou dossier comptable) : les immeubles se répartissent ensuite
        // via le flux « Affecter » de la liste des transactions.
        const affectation = affectationValue(row);
        const type = row.suggestion.type;
        return {
          date_transac: row.date_transac,
          montant: row.montant,
          description: row.description,
          external_id: row.external_id,
          balance:
            affectation && type
              ? {
                  type,
                  domaine: affectation.domaine,
                  id_tenant: affectation.id_tenant,
                  id_bien: affectation.id_bien,
                  id_property_folder: affectation.id_property_folder,
                  comment: row.description,
                }
              : null,
        };
      });

    if (transactions.length === 0) {
      setError("Aucune transaction sélectionnée");
      return;
    }

    setIsLoading(true);
    try {
      const result = await commit({
        id_bank: idBank,
        id_profile_nom: null,
        transactions,
      });
      setImportResult(result);
      onImported();
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setIsLoading(false);
    }
  }

  const selectableItems = useMemo(
    () => rows.map((row) => ({ id: row.tmp_id })),
    [rows],
  );

  // Une ligne est « catégorisée » si elle a une affectation résolue et un type.
  const categorizedCount = rows.filter(
    (row) => affectationValue(row) && row.suggestion.type,
  ).length;
  const uncategorizedCount = rows.length - categorizedCount;

  return (
    <ModalDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) resetAll();
        onOpenChange(nextOpen);
      }}
      size="xl"
      header="Importer un relevé bancaire"
    >
      {importResult ? (
        <>
          <ModalDialogBody className={styles.resultBody}>
            <Callout variant={importResult.created > 0 ? "success" : "warning"}>
              {`${importResult.created} transaction(s) importée(s)` +
                (importResult.skipped_duplicates
                  ? ` — ${importResult.skipped_duplicates} doublon(s) ignoré(s)`
                  : "")}
            </Callout>
            {importResult.skipped_transactions.length > 0 && (
              <Callout variant="warning" title="Doublons ignorés">
                Ces opérations existent déjà sur ce compte :
                <ul>
                  {importResult.skipped_transactions.map((transaction, i) => (
                    <li key={i}>
                      {dateConvert(transaction.date_transac)} —{" "}
                      {transaction.description} (
                      {currencyFormatter(transaction.montant)})
                    </li>
                  ))}
                </ul>
              </Callout>
            )}
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => resetAll()}>
                Importer un autre relevé
              </Button>
            }
          >
            <Button onClick={() => onOpenChange(false)} variant="primary">
              Fermer
            </Button>
          </ModalDialogFooter>
        </>
      ) : (
        step === "upload" &&
        isLoading && (
          <div className={styles.loadingState}>
            <Spinner width="3rem" />
            <Text variant="kilo" weight="medium">
              {files.length > 1
                ? `Analyse de ${files.length} relevés en cours…`
                : "Analyse du relevé en cours…"}
            </Text>
            <Text color="muted">
              Cela peut prendre plusieurs dizaines de secondes, voire plusieurs
              minutes, selon le nombre et la taille des documents. Ne fermez pas
              cette fenêtre.
            </Text>
          </div>
        )
      )}
      {step === "upload" && !isLoading && (
        <>
          <ModalDialogBody>
            <div className={styles.section}>
              <div>
                <Text weight="medium">Compte bancaire</Text>
                <Text color="muted">
                  Associez le relevé à une banque pour activer le dédoublonnage,
                  ou laissez « Sans banque » pour un compte manuel.
                </Text>
              </div>
              <ReactSelectStandalone
                options={bankOptions}
                value={
                  bankOptions.find((opt) => opt.value === idBank) ??
                  bankOptions[0]
                }
                onChange={(opt: { value: number | null } | null) =>
                  setIdBank(opt?.value ?? null)
                }
              />
            </div>
            <div className={styles.sectionSpaced}>
              <div>
                <Text weight="medium">Fichier de relevé</Text>
                <Text color="muted">
                  Seul les PDF sont acceptés pour l'instant.
                </Text>
              </div>
              <FileDropInput
                accept={ACCEPT}
                multiple
                text="Déposez vos relevés ou cliquez pour parcourir"
                format="PDF uniquement"
                onFiles={addFiles}
              />
              {files.length > 0 && (
                <ul className={styles.fileList}>
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}:${f.size}:${i}`}
                      className={styles.fileRow}
                    >
                      <IconFile size={20} stroke={1.5} />
                      <Text as="span" variant="deci" weight="medium">
                        {f.name}
                      </Text>
                      <Text as="span" variant="deci" color="muted">
                        {formatFileSize(f.size)}
                      </Text>
                      <div className={styles.fileRowSpacer} />
                      <IconButton
                        icon={IconX}
                        label="Retirer le fichier"
                        variant="plain"
                        size="sm"
                        onClick={() => removeFile(i)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Callout variant="danger">{error}</Callout>
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
            }
          >
            <Button
              onClick={handlePreview}
              variant="primary"
              disabled={files.length === 0 || isLoading}
            >
              {isLoading
                ? "Analyse en cours…"
                : files.length > 1
                ? `Analyser ${files.length} relevés`
                : "Analyser le relevé"}
            </Button>
          </ModalDialogFooter>
        </>
      )}
      {step === "preview" && (
        <Selectable items={selectableItems} defaultSelectAll rangeSelect>
          <ModalDialogBody className={styles.previewBody}>
            <div className={styles.summaryGrid}>
              <SummaryItem label="Transactions détectées" value={rows.length} />
              <SummaryItem
                label="Catégorisées"
                value={categorizedCount}
                color="success"
              />
              <SummaryItem
                label="À compléter"
                value={uncategorizedCount}
                color={uncategorizedCount > 0 ? "warning" : "muted"}
              />
            </div>

            <Text variant="deci" color="muted">
              Pour répartir une transaction sur plusieurs biens (immeuble),
              importez-la sans affectation puis utilisez « Affecter » depuis la
              liste des transactions.
            </Text>

            <Selectable.Action
              render={(checkedIds) =>
                checkedIds.length > 0 ? (
                  <div className={styles.bulkBar}>
                    <Text weight="medium">
                      {checkedIds.length} ligne(s) sélectionnée(s)
                    </Text>
                    <Button
                      variant="secondary"
                      onClick={() => resetCategorisation(checkedIds)}
                    >
                      Réinitialiser la catégorisation
                    </Button>
                  </div>
                ) : null
              }
            />

            {isLoading && <IsLoading />}

            <Table>
              <Table.Header>
                <Table.Column>
                  <Selectable.Checkbox selectAll />
                </Table.Column>
                <Table.Column>Date</Table.Column>
                <Table.Column>Description</Table.Column>
                <Table.Column align="right">Montant</Table.Column>
                <Table.Column>Affectation</Table.Column>
                <Table.Column>Type</Table.Column>
              </Table.Header>
              <Table.Body>
                {rows.map((row, index) => {
                  // L'affectation effective passe toujours par une option
                  // valide : une suggestion pointant vers un dossier non
                  // proposé (immeuble) se résout à null et n'est pas affectée.
                  const affectation = affectationValue(row);
                  const typeOptions = typeOptionsForDomaine(
                    affectation?.domaine ?? null,
                  );
                  return (
                    <Table.Row key={row.tmp_id}>
                      <Table.Cell>
                        <Selectable.Checkbox value={row.tmp_id} index={index} />
                      </Table.Cell>
                      <Table.Cell>
                        <Text variant="deci">
                          {dateConvert(row.date_transac)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <div className={styles.descriptionCell}>
                          <Text
                            as="span"
                            variant="deci"
                            className={styles.descriptionText}
                            title={row.description}
                          >
                            {row.description}
                          </Text>
                          <div>
                            <SourceBadge source={row.suggestion.source} />
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell align="right">
                        <Text
                          as="span"
                          variant="deci"
                          weight="medium"
                          color={row.montant >= 0 ? "success" : "danger"}
                        >
                          {currencyFormatter(row.montant)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell className={styles.affectationCell}>
                        <ReactSelectStandalone
                          options={affectationOptions}
                          isClearable
                          value={affectation}
                          onChange={(opt: AffectationOption | null) =>
                            setRows((draft) => {
                              const suggestion = draft[index].suggestion;
                              const nextDomaine = opt?.domaine ?? null;
                              // Le type dépend de l'affectation : on le réinitialise
                              // dès que le domaine change pour éviter une combinaison invalide.
                              if (nextDomaine !== suggestion.domaine) {
                                suggestion.type = null;
                              }
                              suggestion.domaine = nextDomaine;
                              suggestion.id_tenant = opt?.id_tenant ?? null;
                              suggestion.id_bien = opt?.id_bien ?? null;
                              suggestion.id_property_folder =
                                opt?.id_property_folder ?? null;
                              suggestion.source = opt
                                ? "manual"
                                : suggestion.type
                                ? suggestion.source
                                : "none";
                            })
                          }
                        />
                      </Table.Cell>
                      <Table.Cell className={styles.selectCell}>
                        <ReactSelectStandalone
                          options={groupBalanceTypeOptionsByFamily(
                            typeOptions,
                            families,
                          )}
                          isClearable
                          isDisabled={!affectation}
                          value={
                            typeOptions.find(
                              (opt) => opt.value === row.suggestion.type,
                            ) ?? null
                          }
                          onChange={(opt: TypeOption | null) =>
                            setRows((draft) => {
                              draft[index].suggestion.type = opt?.value ?? null;
                              if (!opt) {
                                draft[index].suggestion.source = "none";
                              } else {
                                draft[index].suggestion.source = "manual";
                              }
                            })
                          }
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </ModalDialogBody>

          <Callout variant="danger">{error}</Callout>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => setStep("upload")}>
                Retour
              </Button>
            }
          >
            <Selectable.Action
              render={(checkedIds) => (
                <Button
                  onClick={() => handleCommit(checkedIds)}
                  variant="primary"
                  disabled={isLoading || checkedIds.length === 0}
                >
                  {isLoading
                    ? "Import en cours…"
                    : `Importer ${checkedIds.length} transaction(s)`}
                </Button>
              )}
            />
          </ModalDialogFooter>
        </Selectable>
      )}
    </ModalDialog>
  );
}

function SummaryItem({
  label,
  value,
  color = "neutral",
}: {
  label: string;
  value: number;
  color?: "neutral" | "success" | "warning" | "muted";
}) {
  return (
    <div className={styles.summaryItem}>
      <Text color="muted">{label}</Text>
      <Text variant="mega" weight="bold" color={color}>
        {value}
      </Text>
    </div>
  );
}

function SourceBadge({ source }: { source: string }) {
  if (source === "finance_config") {
    return <Status as="span" color="success" text="Règle" />;
  }
  if (source === "tenant_name") {
    return <Status as="span" color="success" text="Nom locataire" />;
  }
  if (source === "openai") {
    return <Status as="span" color="info" text="IA" />;
  }
  if (source === "manual") {
    return <Status as="span" color="neutral" text="Manuel" />;
  }
  return (
    <Text as="span" variant="deci" color="muted">
      Aucune suggestion
    </Text>
  );
}
